import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Home, 
  Heart, 
  Calendar, 
  MessageSquare,
  Sparkles,
  Headphones,
  MapPin
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface NavItem {
  path: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  badge?: number
  accent?: 'kinky' | 'passion' | 'sensual' | 'artistic' | 'luxury'
}

const navigationItems: NavItem[] = [
  {
    path: '/',
    label: 'Home',
    icon: Home,
    accent: 'kinky'
  },
  {
    path: '/dating',
    label: 'Dating',
    icon: Heart,
    accent: 'passion'
  },
  {
    path: '/events',
    label: 'Events',
    icon: Calendar,
    accent: 'sensual'
  },
  {
    path: '/podcasts',
    label: 'Podcasts',
    icon: Headphones,
    accent: 'luxury'
  },
  {
    path: '/chat',
    label: 'Chat',
    icon: MessageSquare,
    accent: 'artistic'
  }
]

export function ModernBottomNav() {
  const location = useLocation()
  const currentPath = location.pathname

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-[200] bg-dark-950/95 backdrop-blur-xl border-t border-kinky-600/20 safe-area-inset-bottom"
    >
      <div className="relative flex items-center justify-around h-16 px-2">
        {navigationItems.map((item) => {
          const isActive = currentPath === item.path
          const Icon = item.icon
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "relative flex flex-col items-center justify-center flex-1 h-full touch-target group transition-all duration-300",
                isActive && "scale-105"
              )}
            >
              {/* Active background glow */}
              <AnimatePresence>
                {isActive && (
                  <motion.div
                    layoutId="activeNavBg"
                    className={cn(
                      "absolute inset-x-2 inset-y-1 rounded-2xl",
                      item.accent === 'kinky' && "bg-gradient-to-br from-kinky-600/20 to-kinky-700/10",
                      item.accent === 'passion' && "bg-gradient-to-br from-passion-600/20 to-passion-700/10",
                      item.accent === 'sensual' && "bg-gradient-to-br from-sensual-600/20 to-sensual-700/10",
                      item.accent === 'artistic' && "bg-gradient-to-br from-artistic-600/20 to-artistic-700/10",
                      item.accent === 'luxury' && "bg-gradient-to-br from-luxury-600/20 to-luxury-700/10"
                    )}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{
                      type: "spring",
                      stiffness: 400,
                      damping: 25
                    }}
                  />
                )}
              </AnimatePresence>

              {/* Icon container */}
              <div className="relative z-10">
                <motion.div
                  whileTap={{ scale: 0.9 }}
                  className="relative"
                >
                  <Icon 
                    className={cn(
                      "w-6 h-6 transition-all duration-300",
                      isActive ? (
                        item.accent === 'kinky' ? "text-kinky-500" :
                        item.accent === 'passion' ? "text-passion-500" :
                        item.accent === 'sensual' ? "text-sensual-500" :
                        item.accent === 'artistic' ? "text-artistic-500" :
                        "text-luxury-500"
                      ) : "text-dark-400 group-hover:text-dark-300"
                    )}
                  />
                  
                  {/* Badge */}
                  {item.badge && item.badge > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className={cn(
                        "absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white rounded-full",
                        item.accent === 'kinky' && "bg-kinky-500",
                        item.accent === 'passion' && "bg-passion-500",
                        item.accent === 'sensual' && "bg-sensual-500",
                        item.accent === 'artistic' && "bg-artistic-500",
                        item.accent === 'luxury' && "bg-luxury-500"
                      )}
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </motion.span>
                  )}
                </motion.div>

                {/* Sparkle effect for active */}
                {isActive && (
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <Sparkles className="w-3 h-3 text-luxury-400 absolute -top-1 -left-1 animate-pulse" />
                  </motion.div>
                )}
              </div>

              {/* Label */}
              <motion.span 
                className={cn(
                  "text-[10px] font-medium mt-1 transition-all duration-300",
                  isActive ? (
                    item.accent === 'kinky' ? "text-kinky-400" :
                    item.accent === 'passion' ? "text-passion-400" :
                    item.accent === 'sensual' ? "text-sensual-400" :
                    item.accent === 'artistic' ? "text-artistic-400" :
                    "text-luxury-400"
                  ) : "text-dark-500"
                )}
                animate={{
                  y: isActive ? -2 : 0
                }}
              >
                {item.label}
              </motion.span>

              {/* Ripple effect */}
              <motion.div
                className={cn(
                  "absolute inset-0 rounded-2xl mx-2 pointer-events-none",
                  item.accent === 'kinky' && "bg-kinky-500",
                  item.accent === 'passion' && "bg-passion-500",
                  item.accent === 'sensual' && "bg-sensual-500",
                  item.accent === 'artistic' && "bg-artistic-500",
                  item.accent === 'luxury' && "bg-luxury-500"
                )}
                initial={{ opacity: 0 }}
                whileTap={{ opacity: 0.2 }}
                transition={{ duration: 0.3 }}
              />
            </Link>
          )
        })}
      </div>
    </nav>
  )
}